import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import SidebarWrapper from '../Containers/SidebarWrapper';
import AdminSidebar from '../AdminSidebar';
import MobileTopBar, { MOBILE_TOP_BAR_HEIGHT } from '../Components/MobileTopBar';
import { hideScrollbarSx } from '../PageContainer';
import { useLanguage } from '../../hooks/useLanguage';

const AdminDashboardLayout: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const { getPathWithoutLang } = useLanguage();
  const path = getPathWithoutLang(location.pathname);
  const isPageEditor = path === '/page-editor';
  const mainContentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0, left: 0 });
  }, [location.pathname, location.search]);

  return (
    <Box
      display="flex"
      height="100vh"
      sx={{ flexDirection: 'row', position: 'relative', overflow: 'hidden' }}
    >
      {isMobile && <MobileTopBar />}

      {/* Sidebar — desktop only (always visible in admin) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'block' },
          width: '25vw',
          position: 'fixed',
          left: 0,
          top: '2vh',
          height: '97vh',
          maxHeight: '97vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <SidebarWrapper compact>
          <AdminSidebar />
        </SidebarWrapper>
      </Box>

      <Box
        component="main"
        ref={mainContentRef}
        sx={(muiTheme) => ({
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          marginLeft: { xs: 0, md: '25vw' },
          height: { xs: `calc(100vh - ${MOBILE_TOP_BAR_HEIGHT}px)`, md: '100vh' },
          marginTop: { xs: `${MOBILE_TOP_BAR_HEIGHT}px`, md: 0 },
          overflowY: isPageEditor ? 'hidden' : 'auto',
          overflowX: 'hidden',
          position: 'relative',
          minHeight: 0,
          width: { xs: '100%', md: 'auto' },
          [muiTheme.breakpoints.down('md')]: hideScrollbarSx,
        })}
      >
        <Box
          sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minHeight: { xs: '100vh', md: 'auto' },
            '& > *': {
              width: '100%',
              minHeight: { xs: '100vh', md: 'auto' },
            },
          }}
        >
          {isPageEditor ? (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Outlet />
            </Box>
          ) : (
            <Outlet />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AdminDashboardLayout;
